import {
  getWebInstrumentations,
  initializeFaro,
  type BeforeSendHook,
  type TransportItem,
} from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'

let initialized = false
const DEFAULT_SAMPLING_RATE = 1
const MAX_STRING_LENGTH = 512
const SENSITIVE_KEY =
  /(?:password|secret|token|authorization|cookie|api[-_]?key|access[-_]?token|refresh[-_]?token|email|phone|first[-_]?name|last[-_]?name|address|ip(?:address)?|user[-_]?id|account[-_]?id|player[-_]?id|tenant[-_]?id|transaction[-_]?id|query|search(?:params)?)/i
const SENSITIVE_OBJECT_KEY =
  /^(?:body|input|raw|request|response|user|profile)$/i
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const BEARER = /\bBearer\s+[^\s]+/gi
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g
const UUID_PATH_SEGMENT =
  /\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?=\/|[?#]|$)/gi
const LONG_NUMERIC_PATH_SEGMENT = /\/\d{6,}(?=\/|[?#]|$)/g
const FIRST_PARTY_API_HOSTS = new Set([
  'api.lunaris.casino',
  'dev.api.lunaris.casino',
  'stg.api.lunaris.casino',
  'server-client.lunaris.casino',
  'dev.server-client.lunaris.casino',
  'stg.server-client.lunaris.casino',
  'aggregator-api.lunaris.casino',
  'dev.aggregator-api.lunaris.casino',
  'stg.aggregator-api.lunaris.casino',
  'bonus-engine-api.lunaris.casino',
  'dev.bonus-engine-api.lunaris.casino',
  'stg.bonus-engine-api.lunaris.casino',
  'reporting.lunaris.casino',
  'dev.reporting.lunaris.casino',
  'stg.reporting.lunaris.casino',
  'admin.api.lunaris.casino',
  'dev.admin.api.lunaris.casino',
  'stg.admin.api.lunaris.casino',
])

export function parseSamplingRate(value: string | undefined): number {
  const normalized = value?.trim()
  if (!normalized) return DEFAULT_SAMPLING_RATE
  const rate = Number(normalized)
  return Number.isFinite(rate) && rate >= 0 && rate <= 1
    ? rate
    : DEFAULT_SAMPLING_RATE
}

function boundedLabel(value: string | undefined, fallback: string): string {
  const label = (value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, '-')
    .slice(0, 64)
  return label || fallback
}

function collectorUrl(value: string | undefined): string | null {
  try {
    const url = new URL(value?.trim() || '')
    if (url.protocol !== 'https:') return null
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return null
  }
}

function redactValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'string') {
    return value
      .replace(UUID_PATH_SEGMENT, '/:id')
      .replace(LONG_NUMERIC_PATH_SEGMENT, '/:id')
      .replace(EMAIL, '[REDACTED_EMAIL]')
      .replace(BEARER, 'Bearer [REDACTED]')
      .replace(JWT, '[REDACTED_TOKEN]')
      .replace(/([?#])[^\s]*/g, '$1[REDACTED]')
      .replace(
        /((?:token|secret|password|authorization|api[-_]?key)=)[^&\s]+/gi,
        '$1[REDACTED]',
      )
      .slice(0, MAX_STRING_LENGTH)
  }
  if (!value || typeof value !== 'object') return value
  if (seen.has(value)) return '[REDACTED_CIRCULAR]'
  seen.add(value)
  if (Array.isArray(value))
    return value.map((entry) => redactValue(entry, seen))
  return Object.fromEntries(
    Object.entries(value).flatMap(([key, entry]) => {
      if (SENSITIVE_OBJECT_KEY.test(key)) return []
      return [
        [
          key,
          SENSITIVE_KEY.test(key) ? '[REDACTED]' : redactValue(entry, seen),
        ],
      ]
    }),
  )
}

export const redactTelemetryItem: BeforeSendHook = (item: TransportItem) =>
  redactValue(item) as TransportItem

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function firstPartyOrigins(values: Array<string | undefined>): RegExp[] {
  return values
    .flatMap((value) => {
      try {
        const rawValue = value?.trim() || ''
        const parsed = new URL(rawValue)
        const authority = rawValue.match(/^https:\/\/([^\/?#]*)/i)?.[1] || ''
        if (
          parsed.protocol !== 'https:' ||
          parsed.port !== '' ||
          authority.includes(':') ||
          authority.includes('@') ||
          !FIRST_PARTY_API_HOSTS.has(parsed.hostname)
        )
          return []
        return [new RegExp(`^${escapeRegExp(parsed.origin)}(?:/|$)`)]
      } catch {
        return []
      }
    })
    .filter(
      (origin, index, origins) =>
        origins.findIndex((candidate) => candidate.source === origin.source) ===
        index,
    )
}

export function initializeObservability(): void {
  if (initialized || typeof window === 'undefined') return

  const url = collectorUrl(process.env.REACT_APP_GRAFANA_FARO_URL)
  if (!url) return

  initialized = true
  try {
    initializeFaro({
      url,
      app: {
        name: boundedLabel(
          process.env.REACT_APP_GRAFANA_FARO_APP_NAME,
          'bonus-engine-fe',
        ),
        version: boundedLabel(
          process.env.REACT_APP_GRAFANA_FARO_VERSION,
          'unknown',
        ),
        environment: boundedLabel(
          process.env.REACT_APP_GRAFANA_FARO_ENVIRONMENT ||
            process.env.REACT_APP_STAGE,
          process.env.NODE_ENV,
        ),
      },
      sessionTracking: {
        enabled: true,
        samplingRate: parseSamplingRate(
          process.env.REACT_APP_GRAFANA_FARO_SAMPLING_RATE,
        ),
      },
      beforeSend: redactTelemetryItem,
      preventGlobalExposure: true,
      ignoreUrls: [url],
      instrumentations: [
        ...getWebInstrumentations(),
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: firstPartyOrigins([
              process.env.REACT_APP_SERVER_MASTER,
              process.env.REACT_APP_SERVER_BONUS_ENGINE,
              process.env.REACT_APP_SERVER_INCENTIVE_API,
              process.env.REACT_APP_SERVER_REPORTS_API,
              process.env.REACT_APP_SERVER_CASINO_API,
            ]),
          },
        }),
      ],
    })
  } catch {
    initialized = false
  }
}
